import styled from 'styled-components';
import Select from 'react-select';
import RegionService from '../../../service/Region';
import {
  ItemStatusList,
  PriceList,
  SidoList,
} from '../../../interface/aiAnalyze/aiRecommend/data';
import { useEffect, useState } from 'react';
import {
  Region,
  RegionProps,
} from '../../../interface/aiAnalyze/aiRecommend/type';

interface FilterBoxProps {
  setPnuCode: (pnuCode: string) => void;
  itemStatus: string;
  setItemStatus: (itemStatus: string) => void;
  price: string;
  setPrice: (price: string) => void;
}
const FilterBox = ({
  setPnuCode,
  itemStatus,
  setItemStatus,
  price,
  setPrice,
}: FilterBoxProps) => {
  const [sido, setSido] = useState('');
  const [sigungu, setSigungu] = useState('');
  const [emd, setEmd] = useState('');
  const [sigunguList, setSigunguList] = useState<RegionProps[] | undefined>([]);
  const [emdList, setEmdList] = useState<RegionProps[] | undefined>([]);
  const [currentRegion, setCurrentRegion] = useState<Region>({
    sido: '',
    sigungu: '',
    emd: '',
  });

  const customStyles = {
    container: (provided: any) => ({
      ...provided,
      flex: 1,
    }),
    control: (provided: any) => ({
      ...provided,
      display: 'flex',
      flexDirection: 'row',
      borderRadius: '8px',
      border: 'none',
      background: '#F7F8FC',
      height: '40px',
      fontFamily: 'Spoqa Han Sans Neo',
      fontSize: '14px',
      fontWeight: 500,
      letterSpacing: '-2%',
      lineHeight: '145%',
      color: '#616161',
      width: '100%',
    }),
    dropdownIndicator: (provided: any) => ({
      ...provided,
      order: 1,
    }),
    indicatorSeparator: (provided: any) => ({
      ...provided,
      display: 'none',
    }),
    option: (provided: any, state: any) => ({
      ...provided,
      fontFamily: 'Spoqa Han Sans Neo',
      fontSize: '14px',
      fontWeight: 500,
      letterSpacing: '-2%',
      lineHeight: '145%',
      backgroundColor: state.isSelected ? '#e6e6e6' : provided.backgroundColor,
      color: state.isSelected ? '#222222' : '#616161',
    }),
  };

  const getRegion = async (code: string) => {
    try {
      const res = await RegionService.list(code);
      return res.data as RegionProps[];
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    if (sido !== '') {
      getRegion(sido).then((res) => setSigunguList(res));
    }
  }, [sido]);

  useEffect(() => {
    if (sigungu !== '') {
      getRegion(sigungu).then((res) => setEmdList(res));
    }
  }, [sigungu]);

  const sidoOptions = SidoList.map((sido) => ({
    value: sido.code,
    label: sido.region,
  }));
  const sigunguOptions = (sigunguList ?? []).map((sigungu) => ({
    value: sigungu.code,
    label: sigungu.region,
  }));
  const emdOptions = (emdList ?? [])
    .filter((emd) => emd.region !== '전체')
    .map((emd) => ({
      value: emd.code,
      label: emd.region,
    }));

  return (
    <Container>
      <RegionSelectBox>
        <Select
          options={sidoOptions}
          value={
            sidoOptions.find((option) => option.label === currentRegion.sido) ||
            null
          }
          styles={customStyles}
          placeholder="시/도"
          onChange={(selectedOption) => {
            if (selectedOption) {
              setSido(selectedOption.value);
              setSigungu('');
              setEmd('');
              setSigunguList([]);
              setEmdList([]);
              setCurrentRegion({
                sido: selectedOption.label,
                sigungu: '',
                emd: '',
              });
              setPnuCode(selectedOption.value);
            }
          }}
        />
        <Select
          options={sigunguOptions}
          value={
            sigunguOptions.find(
              (option) => option.label === currentRegion.sigungu,
            ) || null
          }
          styles={customStyles}
          placeholder="구/군"
          onChange={(selectedOption) => {
            if (selectedOption) {
              setSigungu(selectedOption.value);
              setEmd('');
              setEmdList([]);
              setCurrentRegion({
                ...currentRegion,
                sigungu: selectedOption.label,
                emd: '',
              });
              setPnuCode(selectedOption.value);
            }
          }}
          noOptionsMessage={() => '시/도 먼저 선택'}
        />
        <Select
          options={emdOptions}
          value={
            emdOptions.find((option) => option.label === currentRegion.emd) ||
            null
          }
          styles={{
            ...customStyles,
            control: (provided: any) => ({
              ...customStyles.control(provided),
            }),
          }}
          placeholder="읍/면/동"
          onChange={(selectedOption) => {
            if (selectedOption) {
              setEmd(selectedOption.value);
              setCurrentRegion({
                ...currentRegion,
                emd: selectedOption.label,
              });
              setPnuCode(selectedOption.value);
            }
          }}
          noOptionsMessage={() => '시/군/구 먼저 선택'}
        />
      </RegionSelectBox>
      <RegionSelectBox>
        <Select
          options={ItemStatusList}
          styles={{
            ...customStyles,
            control: (provided: any) => ({
              ...customStyles.control(provided),
            }),
          }}
          placeholder="물건종류"
          value={
            itemStatus === ''
              ? null
              : {
                  label: itemStatus,
                  value: itemStatus,
                }
          }
          onChange={(selectedOption) => {
            if (selectedOption) {
              setItemStatus(selectedOption.label);
            }
          }}
        />
        <Select
          options={PriceList}
          styles={{
            ...customStyles,
            control: (provided: any) => ({
              ...customStyles.control(provided),
            }),
          }}
          placeholder="가격"
          value={
            price === ''
              ? null
              : {
                  label: price,
                  value: price,
                }
          }
          onChange={(selectedOption) => {
            if (selectedOption) {
              setPrice(selectedOption.label);
            }
          }}
        />
      </RegionSelectBox>
    </Container>
  );
};

export default FilterBox;

const Container = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 16px;
  align-items: center;
  padding: 16px;
`;

const RegionSelectBox = styled.div`
  display: flex;
  flex-direction: row;
  gap: 4px;
  width: 100%;
`;
